import { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  message,
  Space,
  Row,
  Col,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';

import { Incident, IncidentType, IncidentPriority, IncidentStatus } from '@/types/community/incident';
import { addIncident, updateIncident } from '@/api/incidents';
import { useAuth } from '@/contexts/AuthContext';
import { BlockApartmentUnitSelector } from '@/components/common/BlockApartmentUnitSelector';
import { AmenitySelector } from '@/components/common/AmenitySelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { VendorSelector } from '@/components/common/VendorSelector';
import type { FileMetadata } from '@/types/common';

const { TextArea } = Input;
const { Title } = Typography;

interface IncidentModalProps {
  open: boolean;
  incident?: Incident | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const INCIDENT_TYPES: { value: IncidentType; label: string }[] = [
  { value: 'safety', label: 'Safety' },
  { value: 'security', label: 'Security' },
  { value: 'untidy', label: 'Untidy' },
  { value: 'noise', label: 'Noise' },
  { value: 'other', label: 'Other' }
];

const INCIDENT_PRIORITIES: { value: IncidentPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
];

const INCIDENT_STATUSES: { value: IncidentStatus; label: string }[] = [
  { value: 'reported', label: 'Reported' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' }
];

const REPORTER_TYPES = [
  { value: 'resident', label: 'Resident' },
  { value: 'employee', label: 'Employee' },
  { value: 'vendor', label: 'Vendor' }
];

export default function IncidentModal({ open, incident, onCancel }: IncidentModalProps) {
  const { t } = useTranslation(['incidentReporting', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [blockData, setBlockData] = useState<{ id: string; name: string } | null>(null);
  const [apartmentUnitData, setApartmentUnitData] = useState<{ id: string; name: string } | null>(null);
  const [amenityData, setAmenityData] = useState<{ id: string; name: string } | null>(null);
  const [reporterType, setReporterType] = useState<'resident' | 'employee' | 'vendor'>('resident');
  const [reportedByData, setReportedByData] = useState<{ id: string; name: string } | null>(null);
  const [assignedToData, setAssignedToData] = useState<{ id: string; name: string } | null>(null);

  // Mutations
  const addIncidentMutation = useMutation({
    mutationFn: (data: Omit<Incident, 'id'>) => addIncident(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('incidentReporting:addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Add incident error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:messages.errorOccurred');
      message.error(`Failed to add incident: ${errorMessage}`);
    }
  });

  const updateIncidentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<Incident, 'id'>> }) => 
      updateIncident(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('incidentReporting:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Update incident error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:messages.errorOccurred');
      message.error(`Failed to update incident: ${errorMessage}`);
    }
  });

  // Initialize form when incident changes
  useEffect(() => {
    if (open) {
      if (incident) {
        form.setFieldsValue({
          title: incident.title,
          description: incident.description,
          type: incident.type,
          priority: incident.priority,
          status: incident.status,
          blockId: incident.block?.id,
          apartmentUnitId: incident.apartmentUnit?.id,
          amenityId: incident.amenity?.id,
          reportedById: incident.reportedBy.id,
          assignedToId: incident.assignedTo?.id,
          reportedAt: incident.reportedAt ? dayjs(incident.reportedAt) : undefined,
          resolvedAt: incident.resolvedAt ? dayjs(incident.resolvedAt) : undefined,
        });

        // Set related data
        setBlockData(incident.block || null);
        setApartmentUnitData(incident.apartmentUnit || null);
        setAmenityData(incident.amenity || null);
        setReportedByData(incident.reportedBy);
        setAssignedToData(incident.assignedTo || null);

        // Set file list
        if (incident.attachments) {
          const files: UploadFile[] = incident.attachments.map((attachment, index) => ({
            uid: `-${index}`,
            name: attachment.name,
            status: 'done',
            url: attachment.url
          }));
          setFileList(files);
        }
      } else {
        form.resetFields();
        setFileList([]);
        setBlockData(null);
        setApartmentUnitData(null);
        setAmenityData(null);
        setReporterType('resident');
        setReportedByData(null);
        setAssignedToData(null);
      }
    }
  }, [incident, form, open]);

  const handleSubmit = async (values: any) => {
    if (!firestoreUser) {
      message.error(t('common:messages.notAuthenticated'));
      return;
    }

    // Additional client-side validation
    if (!values.title?.trim()) {
      message.error(t('incidentReporting:validationErrors.titleRequired'));
      return;
    }
    if (!values.description?.trim()) {
      message.error(t('incidentReporting:validationErrors.descriptionRequired'));
      return;
    }
    if (!values.type) {
      message.error(t('incidentReporting:validationErrors.typeRequired'));
      return;
    }
    if (!values.priority) {
      message.error(t('incidentReporting:validationErrors.priorityRequired'));
      return;
    }
    if (!values.status) {
      message.error(t('incidentReporting:validationErrors.statusRequired'));
      return;
    }
    if (!reportedByData?.id) {
      message.error(t('incidentReporting:validationErrors.reporterRequired'));
      return;
    }
    if (!values.reportedAt) {
      message.error(t('incidentReporting:validationErrors.reportDateRequired'));
      return;
    }

    try {
      // Convert file list to FileMetadata
      const attachments: FileMetadata[] = fileList
        .filter(file => file.status === 'done')
        .map(file => ({
          id: file.uid || `file-${Date.now()}-${Math.random()}`,
          name: file.name || '',
          url: file.url || '',
          size: file.size || 0,
          type: file.type || '',
          path: file.url || '',
          uploadedAt: new Date(),
          mimeType: file.type || ''
        }));

      const incidentData: Omit<Incident, 'id'> = {
        title: values.title.trim(),
        description: values.description.trim(),
        type: values.type,
        priority: values.priority,
        status: values.status,
        reportedBy: { ...reportedByData!, type: reporterType },
        reportedAt: values.reportedAt?.toDate() || new Date(),
        block: blockData || undefined,
        apartmentUnit: apartmentUnitData || undefined,
        amenity: amenityData || undefined,
        assignedTo: assignedToData || undefined,
        resolvedAt: values.resolvedAt?.toDate() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        workspace: firestoreUser!.workspace!
      };

      console.log('Submitting incident data:', incidentData);

      if (incident) {
        updateIncidentMutation.mutate({ id: incident.id, data: incidentData });
      } else {
        addIncidentMutation.mutate(incidentData);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(`Form submission failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setBlockData(null);
    setApartmentUnitData(null);
    setAmenityData(null);
    setReporterType('resident');
    setReportedByData(null);
    setAssignedToData(null);
    onCancel();
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const uploadProps = {
    beforeUpload: () => false, // Prevent auto upload
    fileList,
    onChange: handleFileChange,
    multiple: true
  };

  const renderReporterSelector = () => {
    switch (reporterType) {
      case 'resident':
        return <ResidentSelector fieldName="reportedById" onResidentDataChange={setReportedByData} />;
      case 'employee':
        return <EmployeeSelector fieldName="reportedById" onEmployeeDataChange={setReportedByData} />;
      case 'vendor':
        return <VendorSelector fieldName="reportedById" onVendorDataChange={setReportedByData} />;
      default:
        return <ResidentSelector fieldName="reportedById" onResidentDataChange={setReportedByData} />;
    }
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {incident ? t('incidentReporting:editIncident') : t('incidentReporting:addIncident')}
        </Title>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      {open && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'reported',
            priority: 'medium',
            type: 'other',
            reportedAt: dayjs()
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label={t('incidentReporting:title')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Input placeholder={t('incidentReporting:titlePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label={t('incidentReporting:type')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Select placeholder={t('incidentReporting:selectType')}>
                  {INCIDENT_TYPES.map(type => (
                    <Select.Option key={type.value} value={type.value}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label={t('incidentReporting:priority')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Select placeholder={t('incidentReporting:selectPriority')}>
                  {INCIDENT_PRIORITIES.map(priority => (
                    <Select.Option key={priority.value} value={priority.value}>
                      {priority.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('incidentReporting:status')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Select placeholder={t('incidentReporting:selectStatus')}>
                  {INCIDENT_STATUSES.map(status => (
                    <Select.Option key={status.value} value={status.value}>
                      {status.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t('incidentReporting:description')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <TextArea 
              rows={4} 
              placeholder={t('incidentReporting:descriptionPlaceholder')} 
            />
          </Form.Item>

          <BlockApartmentUnitSelector
            onBlockDataChange={setBlockData}
            onApartmentUnitDataChange={setApartmentUnitData}
          />

          <Form.Item
            name="amenityId"
            label={t('incidentReporting:amenity')}
          >
            <AmenitySelector onAmenityDataChange={setAmenityData} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label={t('incidentReporting:reporterType')}
              >
                <Select
                  value={reporterType}
                  onChange={setReporterType}
                  placeholder={t('incidentReporting:selectReporterType')}
                >
                  {REPORTER_TYPES.map(type => (
                    <Select.Option key={type.value} value={type.value}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="reportedById"
                label={t('incidentReporting:reportedBy')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                {renderReporterSelector()}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="assignedToId"
                label={t('incidentReporting:assignedTo')}
              >
                <EmployeeSelector fieldName="assignedToId" onEmployeeDataChange={setAssignedToData} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="reportedAt"
                label={t('incidentReporting:reportedAt')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }} 
                  placeholder={t('incidentReporting:selectReportedAt')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="resolvedAt"
                label={t('incidentReporting:resolvedAt')}
              >
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }} 
                  placeholder={t('incidentReporting:selectResolvedAt')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label={t('incidentReporting:notes')}
          >
            <TextArea 
              rows={3} 
              placeholder={t('incidentReporting:notesPlaceholder')} 
            />
          </Form.Item>

          <Form.Item
            label={t('incidentReporting:attachments')}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                {t('common:actions.upload')}
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                {t('common:actions.cancel')}
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={addIncidentMutation.isPending || updateIncidentMutation.isPending}
              >
                {incident ? t('common:actions.update') : t('common:actions.add')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
} 